import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { 
  Upload, 
  TrendingDown, 
  TrendingUp,
  Calendar,
  Trophy,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  X,
  FileText,
  Download,
  FileSpreadsheet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { AIInsights } from "@/components/AIInsights";
import { format, subDays } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format price in INR with 2 decimal places
function formatPrice(price) {
  if (price === null || price === undefined) return "—";
  return `₹${price.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Calculate percentage change
function calcPctChange(current, previous) {
  if (!current || !previous) return null;
  return ((current - previous) / previous * 100).toFixed(1);
}

// Price change pill component
function ChangePill({ current, previous, showAmount = true }) {
  if (!current || !previous) return <span className="text-[#8a8278] text-xs">—</span>;
  
  const diff = current - previous;
  const pct = calcPctChange(current, previous);
  
  if (diff > 0) {
    return (
      <span className="pill pill-up">
        ▲ {showAmount && `₹${Math.abs(diff).toLocaleString("en-IN")} `}({Math.abs(pct)}%)
      </span>
    );
  }
  if (diff < 0) {
    return (
      <span className="pill pill-down">
        ▼ {showAmount && `₹${Math.abs(diff).toLocaleString("en-IN")} `}({Math.abs(pct)}%)
      </span>
    );
  }
  return <span className="pill pill-same">— flat</span>;
}

// Hidden Markdown Badge
function HiddenMarkdownBadge({ price }) {
  if (!price || price % 1 === 0) return null;
  const paise = Math.round((price % 1) * 100);
  
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">
      🔻 Hidden ₹{paise}p
    </span>
  );
}

// BAU comparison badge
function BAUBadge({ current, bau }) {
  if (!bau) return <span className="text-[#8a8278] text-xs italic">No BAU</span>;
  if (!current) return <span className="text-[#8a8278] text-xs">—</span>;
  
  const diff = current - bau;
  const pct = calcPctChange(current, bau);
  
  if (diff < 0) {
    return (
      <span className="bau-badge bau-below-badge">
        🔴 ▼ ₹{Math.abs(diff).toLocaleString("en-IN")} ({Math.abs(pct)}%)
      </span>
    );
  }
  return (
    <span className="bau-badge bau-above-badge">
      ✅ ▲ ₹{diff.toLocaleString("en-IN")} ({pct}%)
    </span>
  );
}

// Low price badge
function LowBadge({ current, low, type }) {
  if (!low) return <span className="text-[#8a8278] text-xs">—</span>;
  
  const isAtLow = current && current <= low;
  
  if (isAtLow) {
    return (
      <span className="low-badge is-lowest">
        🏆 ₹{low.toLocaleString("en-IN")}
      </span>
    );
  }
  
  return (
    <span className={cn("low-badge", type === "week" ? "low-week" : "low-month")}>
      ₹{low.toLocaleString("en-IN")}
    </span>
  );
}

export default function Dashboard() {
  const { searchQuery, stats, refreshStats, isAdmin } = useOutletContext() || {};
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(6); // Today is index 6
  const [activeTab, setActiveTab] = useState("own"); // "own" or "competition"
  const [decimalAlerts, setDecimalAlerts] = useState({ own: [], competition: [], total: 0 });
  const fileInputRef = useRef(null);

  // Generate last 7 days
  const days = Array.from({ length: 7 }, (_, i) => subDays(new Date(), 6 - i));
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    fetchProducts();
    fetchDecimalAlerts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const fetchDecimalAlerts = async () => {
    try {
      const response = await axios.get(`${API}/decimal-alerts`);
      setDecimalAlerts(response.data);
    } catch (error) {
      console.error("Error fetching decimal alerts:", error);
    }
  };

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem('pricelens_token');
      const response = await axios.get(`${API}/export/excel`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PriceLens_Report_${format(new Date(), 'yyyyMMdd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel report downloaded");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export Excel");
    }
  };

  const exportPDF = async () => {
    try {
      const token = localStorage.getItem('pricelens_token');
      const response = await axios.get(`${API}/export/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PriceLens_Report_${format(new Date(), 'yyyyMMdd')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF report downloaded");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export PDF");
    }
  };

  // Filter products by tab (own/competition)
  const filteredByTab = products.filter(p => p.product_type === activeTab);
  
  // Group products by category - exclude generic "My Products" and "Competition" labels
  const groupedProducts = filteredByTab.reduce((acc, product) => {
    const category = product.category;
    // Skip products without a specific category - they'll only show in Hidden Markdown if they have decimal prices
    if (!category || category === 'My Products' || category === 'Competition') return acc;
    if (!acc[category]) acc[category] = [];
    acc[category].push(product);
    return acc;
  }, {});

  // Custom category order
  const categoryOrder = ['Mixer Grinder', 'Pressure Cooker', 'Gas Stove', 'Wet Grinder'];
  
  // Sort categories by custom order, then alphabetically for any others
  const sortedCategories = Object.keys(groupedProducts).sort((a, b) => {
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);
    
    // If both are in the custom order, sort by their position
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    // If only A is in custom order, A comes first
    if (indexA !== -1) return -1;
    // If only B is in custom order, B comes first
    if (indexB !== -1) return 1;
    // Otherwise sort alphabetically
    return a.localeCompare(b);
  });

  // Filter by search
  const filterProducts = (prods) => {
    if (!searchQuery) return prods;
    return prods.filter(p => 
      (p.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (p.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      p.asin.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  // Handle BAU file upload
  const handleBAUUpload = async () => {
    if (!uploadedFile) {
      toast.error("Please select a file first");
      return;
    }

    setUploading(true);
    try {
      let items = [];
      const fileName = uploadedFile.name.toLowerCase();
      
      // Check if Excel file
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        // Parse Excel file
        const arrayBuffer = await uploadedFile.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        
        // Process rows (skip header)
        data.forEach((row, i) => {
          if (i === 0 || !row || row.length < 2) return; // Skip header and empty rows
          const asin = String(row[0] || '').trim();
          const bauPrice = parseFloat(row[row.length - 1]);
          if (asin && !isNaN(bauPrice)) {
            items.push({
              asin,
              bau_price: bauPrice,
              name: row[1] ? String(row[1]).trim() : null,
              category: row[2] ? String(row[2]).trim() : null,
            });
          }
        });
      } else {
        // Parse CSV file
        const text = await uploadedFile.text();
        const lines = text.trim().split('\n');

        lines.forEach((line, i) => {
          if (i === 0) return; // Skip header
          const cols = line.split(',').map(c => c.trim().replace(/"/g, ''));
          if (cols.length >= 2) {
            const asin = cols[0];
            const bauPrice = parseFloat(cols[cols.length - 1]);
            if (asin && !isNaN(bauPrice)) {
              items.push({
                asin,
                bau_price: bauPrice,
                name: cols[1] || null,
                category: cols[2] || null,
              });
            }
          }
        });
      }

      if (items.length === 0) {
        toast.error("No valid data found in file");
        return;
      }

      const token = localStorage.getItem('pricelens_token');
      const response = await axios.post(`${API}/bau/upload`, { items }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      toast.success(`Loaded BAU prices for ${response.data.updated + response.data.created} products`);
      setUploadOpen(false);
      setUploadedFile(null);
      fetchProducts();
      refreshStats?.();
    } catch (error) {
      console.error("Error uploading BAU:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required to upload BAU");
      } else {
        toast.error("Failed to upload BAU file");
      }
    } finally {
      setUploading(false);
    }
  };

  // Calculate stats from filtered products (by tab)
  const belowBAU = filteredByTab.filter(p => p.bau_price && p.current_price && p.current_price < p.bau_price).length;
  const priceDrops = filteredByTab.filter(p => p.yesterday_price && p.current_price && p.current_price < p.yesterday_price).length;
  const downVsWeek = filteredByTab.filter(p => p.week_ago_price && p.current_price && p.current_price < p.week_ago_price).length;
  const downVsMonth = filteredByTab.filter(p => p.month_ago_price && p.current_price && p.current_price < p.month_ago_price).length;
  const atWeeklyLow = filteredByTab.filter(p => p.lowest_price_7d && p.current_price && p.current_price <= p.lowest_price_7d).length;
  const atMonthlyLow = filteredByTab.filter(p => p.lowest_price_30d && p.current_price && p.current_price <= p.lowest_price_30d).length;
  const bauLoaded = filteredByTab.some(p => p.bau_price);
  
  // Count products by type
  const ownCount = products.filter(p => p.product_type === 'own').length;
  const competitionCount = products.filter(p => p.product_type === 'competition').length;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Day Strip */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
        {days.map((day, i) => (
          <button
            key={i}
            onClick={() => setSelectedDay(i)}
            className={cn(
              "day-chip",
              selectedDay === i && "active"
            )}
          >
            <div className="text-[10px] font-bold uppercase opacity-60">
              {dayNames[day.getDay()]}
            </div>
            <div className="text-base font-extrabold leading-tight">
              {day.getDate()}
            </div>
            <div className="text-[10px] opacity-55">
              {format(day, 'MMM')}
            </div>
          </button>
        ))}
      </div>

      {/* Tabs for My Products / Competition */}
      <div className="flex items-center gap-1 bg-[#e8e5e0] p-1 rounded-lg w-fit">
        <button
          data-testid="tab-own"
          onClick={() => setActiveTab("own")}
          className={cn(
            "px-4 py-2 rounded-md text-sm font-semibold transition-all",
            activeTab === "own"
              ? "bg-white text-[#1a1814] shadow-sm"
              : "text-[#8a8278] hover:text-[#1a1814]"
          )}
        >
          📦 My Products
          <span className={cn(
            "ml-2 px-2 py-0.5 rounded-full text-xs",
            activeTab === "own" ? "bg-blue-100 text-blue-700" : "bg-[#d8d5d0] text-[#8a8278]"
          )}>
            {ownCount}
          </span>
        </button>
        <button
          data-testid="tab-competition"
          onClick={() => setActiveTab("competition")}
          className={cn(
            "px-4 py-2 rounded-md text-sm font-semibold transition-all",
            activeTab === "competition"
              ? "bg-white text-[#1a1814] shadow-sm"
              : "text-[#8a8278] hover:text-[#1a1814]"
          )}
        >
          🎯 Competition
          <span className={cn(
            "ml-2 px-2 py-0.5 rounded-full text-xs",
            activeTab === "competition" ? "bg-amber-100 text-amber-700" : "bg-[#d8d5d0] text-[#8a8278]"
          )}>
            {competitionCount}
          </span>
        </button>
      </div>

      {/* Day Title */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1814]">
            {selectedDay === 6 ? 'Today — ' : ''}{format(days[selectedDay], 'EEE, d MMM yyyy')}
          </h1>
          <p className="text-sm text-[#8a8278]">
            {activeTab === "own" ? "Your products" : "Competition products"} — {selectedDay === 6 ? 'Live price comparison vs BAU' : 'Historical data'}
          </p>
        </div>
        
        {/* Export Button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              data-testid="export-btn"
              variant="outline"
              className="border-[#e0dbd2] hover:bg-[#f5f0e6] text-[#1a1814]"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white">
            <DropdownMenuItem onClick={exportExcel} className="cursor-pointer">
              <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
              Download Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportPDF} className="cursor-pointer">
              <FileText className="h-4 w-4 mr-2 text-red-600" />
              Download PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="sum-card alert-card">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#8a8278] mb-1">
            🔴 Below BAU
          </div>
          <div className={cn("text-2xl font-extrabold font-mono", bauLoaded ? "text-[#c0392b]" : "text-[#8a8278]")}>
            {bauLoaded ? `${belowBAU} items` : 'No BAU'}
          </div>
          <div className="text-[11px] text-[#8a8278] mt-0.5">Products below BAU price</div>
        </div>

        <div className="sum-card">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#8a8278] mb-1">
            📉 Price Drops Today
          </div>
          <div className="text-2xl font-extrabold font-mono text-[#1a7a3c]">
            {priceDrops} items
          </div>
          <div className="text-[11px] text-[#8a8278] mt-0.5">vs yesterday</div>
        </div>

        <div className="sum-card">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#8a8278] mb-1">
            📅 Down vs Last Week
          </div>
          <div className="text-2xl font-extrabold font-mono text-[#1a7a3c]">
            {downVsWeek} items
          </div>
          <div className="text-[11px] text-[#8a8278] mt-0.5">products cheaper than 7d ago</div>
        </div>

        <div className="sum-card">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#8a8278] mb-1">
            📆 Down vs Last Month
          </div>
          <div className="text-2xl font-extrabold font-mono text-[#1a7a3c]">
            {downVsMonth} items
          </div>
          <div className="text-[11px] text-[#8a8278] mt-0.5">products cheaper than 30d ago</div>
        </div>

        <div className="sum-card">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#8a8278] mb-1">
            🏆 At Weekly Low
          </div>
          <div className="text-2xl font-extrabold font-mono text-[#1a1814]">
            {atWeeklyLow} items
          </div>
          <div className="text-[11px] text-[#8a8278] mt-0.5">at or near 7-day low</div>
        </div>

        <div className="sum-card">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#8a8278] mb-1">
            🏆 At Monthly Low
          </div>
          <div className="text-2xl font-extrabold font-mono text-[#1a1814]">
            {atMonthlyLow} items
          </div>
          <div className="text-[11px] text-[#8a8278] mt-0.5">at or near 30-day low</div>
        </div>
      </div>

      {/* AI Daily Insights */}
      <AIInsights />

      {/* Legend */}
      <div className="flex flex-wrap gap-4 items-center text-xs text-[#8a8278]">
        <span className="font-bold text-[#1a1814]">Legend:</span>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[#c0392b]/15 border border-[#c0392b]/40" />
          Row highlighted = price below BAU
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-white border border-[#e0dbd2]" />
          Normal = price at or above BAU
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[#c0392b] font-bold">▲</span> Price increased
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[#1a7a3c] font-bold">▼</span> Price decreased
        </div>
        <div className="flex items-center gap-1.5">
          <span className="low-badge is-lowest text-[10px]">🏆 Low</span> At period low
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">🔻 Hidden</span> Decimal price (hidden markdown)
        </div>
      </div>

      {/* Upload BAU Button - Admin Only */}
      {!bauLoaded && isAdmin && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <div>
              <p className="font-semibold text-amber-800">No BAU file loaded</p>
              <p className="text-sm text-amber-600">Upload your BAU prices to enable price comparison</p>
            </div>
          </div>
          <Button
            data-testid="upload-bau-btn"
            onClick={() => setUploadOpen(true)}
            className="bg-[#f0a500] hover:bg-[#ffc533] text-[#1a1200] font-bold"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload BAU File
          </Button>
        </div>
      )}
      
      {/* Viewer notice when BAU not loaded */}
      {!bauLoaded && !isAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-blue-500" />
          <div>
            <p className="font-semibold text-blue-800">BAU prices not configured</p>
            <p className="text-sm text-blue-600">Contact admin to upload BAU price file</p>
          </div>
        </div>
      )}

      {/* Hidden Markdown Section - Shows products with decimal prices within current tab */}
      {(activeTab === "own" ? decimalAlerts.own : decimalAlerts.competition).length > 0 && (
        <div className="space-y-2" data-testid="hidden-markdown-section">
          {/* Section Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="cat-pip bg-red-500" />
              <h2 className="text-lg font-extrabold tracking-tight text-red-700">
                💰 Hidden Markdown Detected
              </h2>
            </div>
            <span className="text-xs text-red-600 font-semibold">
              {(activeTab === "own" ? decimalAlerts.own : decimalAlerts.competition).length} products with decimal prices
            </span>
          </div>
          
          <p className="text-xs text-[#8a8278] -mt-1">
            Products with non-integer prices (e.g., ₹2,499.60) often indicate temporary/hidden markdowns from Amazon.
          </p>

          {/* Hidden Markdown Table */}
          <div className="tbl-wrap border-red-200 bg-red-50/30">
            <table>
              <thead>
                <tr className="col-group-header !bg-red-100">
                  <th colSpan={2}>Product Info</th>
                  <th colSpan={3} className="text-red-700">Hidden Markdown Details</th>
                  {activeTab === "own" && <th colSpan={2} className="text-[#c0392b]">BAU Comparison</th>}
                </tr>
                <tr className="!bg-red-50">
                  <th style={{ minWidth: 220 }}>Product</th>
                  <th>Category</th>
                  <th className="text-right text-red-700">Exact Price ₹</th>
                  <th className="text-center text-red-700">Paise</th>
                  <th className="text-center">Days Active</th>
                  {activeTab === "own" && <th className="text-right text-[#c0392b]">BAU ₹</th>}
                  {activeTab === "own" && <th className="text-center text-[#c0392b]">% Below BAU</th>}
                </tr>
              </thead>
              <tbody>
                {(activeTab === "own" ? decimalAlerts.own : decimalAlerts.competition).map((alert) => (
                  <tr key={alert.asin} className="hover:bg-red-100/50">
                    <td>
                      <div className="flex items-center gap-2.5">
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0",
                          activeTab === 'own' ? "bg-red-100" : "bg-orange-100"
                        )}>
                          {activeTab === 'own' ? '🔻' : '⚠️'}
                        </div>
                        <div>
                          <div className="font-semibold text-sm leading-tight">
                            {alert.name?.substring(0, 35) || 'Unknown Product'}...
                          </div>
                          <div className="text-[10px] text-[#8a8278] font-mono">{alert.asin}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-xs text-[#8a8278]">{alert.category || '—'}</td>
                    <td className="text-right font-mono font-bold text-red-600">
                      ₹{alert.current_price?.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">
                        {alert.paise_value}p
                      </span>
                    </td>
                    <td className="text-center text-xs text-[#8a8278]">{alert.days_decimal}d</td>
                    {activeTab === "own" && (
                      <td className="text-right font-mono text-xs text-[#8a8278]">
                        {alert.bau_price ? `₹${alert.bau_price.toLocaleString("en-IN")}` : '—'}
                      </td>
                    )}
                    {activeTab === "own" && (
                      <td className="text-center">
                        {alert.pct_below_bau ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                            ▼ {alert.pct_below_bau}%
                          </span>
                        ) : '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Product Tables by Category */}
      {Object.keys(groupedProducts).length === 0 ? (
        <div className="tbl-wrap">
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-[#8a8278]" />
            <h3 className="text-lg font-semibold mb-2">No products added yet</h3>
            <p className="text-[#8a8278] mb-4">Add your first product ASIN to start tracking prices</p>
            <Button asChild className="bg-[#f0a500] hover:bg-[#ffc533] text-[#1a1200]">
              <a href="/products">Add Products</a>
            </Button>
          </div>
        </div>
      ) : (
        sortedCategories.map((category) => {
          const prods = groupedProducts[category];
          const filtered = filterProducts(prods);
          const categoryBelowBAU = filtered.filter(p => p.bau_price && p.current_price && p.current_price < p.bau_price).length;
          
          return (
            <div key={category} className="space-y-2" id={`section-${category.toLowerCase().replace(/\s+/g, '-')}`}>
              {/* Section Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "cat-pip",
                    category === 'Mixer Grinder' ? "bg-blue-500" : 
                    category === 'Pressure Cooker' ? "bg-emerald-500" :
                    category === 'Gas Stove' ? "bg-amber-500" :
                    category === 'Wet Grinder' ? "bg-purple-500" : "bg-slate-500"
                  )} />
                  <h2 className="text-lg font-extrabold tracking-tight">{category}</h2>
                </div>
                <span className="text-xs text-[#8a8278]">
                  {filtered.length} products · {categoryBelowBAU} below BAU
                </span>
              </div>

              {/* Table */}
              <div className="tbl-wrap">
                <table>
                  <thead>
                    {/* Column Group Headers */}
                    <tr className="col-group-header">
                      <th colSpan={3}>Product Info</th>
                      <th colSpan={2} className="text-[#c0392b]">BAU Comparison</th>
                      <th colSpan={3}>Day Change</th>
                      <th colSpan={3} className="text-[#1a4a8a]">vs Last Week (7d)</th>
                      <th colSpan={3} className="text-[#b45309]">vs Last Month (30d)</th>
                    </tr>
                    {/* Column Headers */}
                    <tr>
                      <th style={{ minWidth: 220 }}>Product</th>
                      <th>Specs</th>
                      <th className="text-right">Today ₹</th>
                      <th className="text-right text-[#c0392b]">BAU ₹</th>
                      <th className="text-center text-[#c0392b]">vs BAU</th>
                      <th className="text-right">Yesterday ₹</th>
                      <th className="text-center">Day Chg</th>
                      <th className="text-center">Day %</th>
                      <th className="text-right text-[#1a4a8a]">7d Ago ₹</th>
                      <th className="text-center text-[#1a4a8a]">Week Chg</th>
                      <th className="text-center text-[#1a4a8a]">7d Low ₹</th>
                      <th className="text-right text-[#b45309]">30d Ago ₹</th>
                      <th className="text-center text-[#b45309]">Month Chg</th>
                      <th className="text-center text-[#b45309]">30d Low ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((product) => {
                      const isBelowBAU = product.bau_price && product.current_price && product.current_price < product.bau_price;
                      const hasDecimalPrice = product.has_decimal_price || (product.current_price && product.current_price % 1 !== 0);
                      
                      return (
                        <tr 
                          key={product.id} 
                          data-testid={`product-row-${product.asin}`}
                          className={cn(isBelowBAU && "bau-alert-row")}
                        >
                          {/* Product */}
                          <td>
                            <div className="flex items-center gap-2.5">
                              <div className={cn(
                                "w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0",
                                product.product_type === 'own' ? "bg-blue-50" : "bg-amber-50"
                              )}>
                                {product.product_type === 'own' ? '📦' : '🎯'}
                              </div>
                              <div>
                                <div className="font-semibold text-sm leading-tight flex items-center gap-2">
                                  {product.title || product.name || 'Unnamed Product'}
                                  {hasDecimalPrice && <HiddenMarkdownBadge price={product.current_price} />}
                                </div>
                                <div className="text-[11px] text-[#8a8278]">{product.brand || '—'}</div>
                                <div className="text-[10px] text-[#8a8278] font-mono">{product.asin}</div>
                              </div>
                            </div>
                          </td>
                          
                          {/* Specs */}
                          <td className="text-xs text-[#8a8278]">
                            {product.specs || product.category || '—'}
                          </td>
                          
                          {/* Today Price */}
                          <td className="text-right font-mono font-bold text-sm">
                            {formatPrice(product.current_price)}
                          </td>
                          
                          {/* BAU Price */}
                          <td className="text-right font-mono text-xs text-[#8a8278]">
                            {formatPrice(product.bau_price)}
                          </td>
                          
                          {/* vs BAU */}
                          <td className="text-center">
                            <BAUBadge current={product.current_price} bau={product.bau_price} />
                          </td>
                          
                          {/* Yesterday Price */}
                          <td className="text-right font-mono text-xs text-[#8a8278]">
                            {formatPrice(product.yesterday_price)}
                          </td>
                          
                          {/* Day Change */}
                          <td className="text-center">
                            <ChangePill current={product.current_price} previous={product.yesterday_price} />
                          </td>
                          
                          {/* Day % */}
                          <td className="text-center text-xs text-[#8a8278]">
                            {product.price_change_24h ? `${product.price_change_24h.toFixed(1)}%` : '—'}
                          </td>
                          
                          {/* 7d Ago Price */}
                          <td className="text-right font-mono text-xs text-[#1a4a8a]">
                            {formatPrice(product.week_ago_price)}
                          </td>
                          
                          {/* Week Change */}
                          <td className="text-center">
                            <ChangePill current={product.current_price} previous={product.week_ago_price} />
                          </td>
                          
                          {/* 7d Low */}
                          <td className="text-center">
                            <LowBadge current={product.current_price} low={product.lowest_price_7d} type="week" />
                          </td>
                          
                          {/* 30d Ago Price */}
                          <td className="text-right font-mono text-xs text-[#b45309]">
                            {formatPrice(product.month_ago_price)}
                          </td>
                          
                          {/* Month Change */}
                          <td className="text-center">
                            <ChangePill current={product.current_price} previous={product.month_ago_price} />
                          </td>
                          
                          {/* 30d Low */}
                          <td className="text-center">
                            <LowBadge current={product.current_price} low={product.lowest_price_30d} type="month" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      {/* Upload BAU Modal */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-white border-[#e0dbd2] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-extrabold">📂 Upload BAU Price File</DialogTitle>
            <DialogDescription className="text-sm text-[#8a8278]">
              Upload your CSV file containing ASIN and BAU (Business As Usual) prices. 
              The dashboard will automatically highlight products trading below BAU.
            </DialogDescription>
          </DialogHeader>

          {/* Sample Format */}
          <div className="bg-[#f2f0ec] rounded-lg p-3 font-mono text-xs text-[#1a1814] leading-relaxed">
            ASIN, Product Name, Category, BAU Price<br/>
            B08XYZ1234, Nutri-Blend Pro 750W, Mixer Grinder, 3800<br/>
            B07ABC5678, AlphaChef 5L Cooker, Pressure Cooker, 1400
          </div>

          {/* Drop Zone */}
          <div 
            className="drop-zone"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#f0a500'; }}
            onDragLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '';
              const file = e.dataTransfer.files[0];
              if (file) setUploadedFile(file);
            }}
          >
            <div className="text-3xl mb-2">📄</div>
            <div className="text-sm text-[#8a8278]">
              {uploadedFile ? `📄 ${uploadedFile.name} selected` : 'Click to browse or drag & drop your file'}
            </div>
            <div className="text-xs text-[#8a8278] mt-1 opacity-70">
              Supports .csv, .xlsx, .xls
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />

          {uploadedFile && (
            <div className="bg-green-50 text-green-700 text-sm p-2 rounded-lg">
              ✅ File ready. Click "Load BAU Prices" to apply.
            </div>
          )}

          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" className="border-[#e0dbd2]">Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleBAUUpload}
              disabled={uploading || !uploadedFile}
              className="bg-[#1a1814] hover:bg-[#333] text-white font-bold"
            >
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load BAU Prices'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
